// Policy decisions: the tail of what the scheduler decided and why. The
// "why" column is the point -- a decision without its reason is not
// reviewable -- so reason gets the wide cell and everything else is dim.
//
// GET-only: the tick loop's pause/resume endpoints exist (Phase 7) but are
// not on the admin proxy's allow-list, and the cockpit does not get to
// change scheduling from a page.
import { useEffect, useState } from "react";
import { fetchDecisions, type DecisionsEnvelope, type PolicyDecision } from "../../api/admin";
import { formatSince } from "../../lib/format";

const REFRESH_MS = 10000;

export default function AdminPolicy() {
  const [data, setData] = useState<DecisionsEnvelope | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [leases, setLeases] = useState(false);

  useEffect(() => {
    const load = () =>
      fetchDecisions()
        .then((d) => {
          setData(d);
          setError(null);
        })
        .catch((e) => setError(e instanceof Error ? e.message : "failed to load decisions"))
        .finally(() => setLoading(false));
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  // Policy appends; newest first here. Its log lives in memory since Policy
  // last started (the last 500), and every tick re-issues each lease.
  const all: PolicyDecision[] = (data?.decisions ?? []).slice().reverse();
  const decisions = leases ? all : all.filter((d) => d.decision !== "LEASE");

  return (
    <>
      <div className="a-head">
        <div>
          <h1 className="a-h1">Policy decisions</h1>
          <p className="a-lede">
            The scheduler's decisions since Policy last started (up to 500), newest first, each with
            the reason it gave. This is read-only: nothing here changes the tick loop.
          </p>
        </div>
        <span className="a-panel__meta">
          tick loop <span className={data?.paused ? "a-state a-state--tripped" : "a-state a-state--running"}>{data?.paused ? "paused" : "running"}</span>
        </span>
      </div>

      {error && <p className="a-error">{error}</p>}

      <div className="a-panel">
        <div className="a-panel__head">
          <span className="a-panel__title">Decision log</span>
          <span className="a-panel__meta">
            {decisions.length} of {all.length} entries{" "}
            <button className="a-btn" onClick={() => setLeases((v) => !v)}>
              {leases ? "Hide leases" : "Show leases"}
            </button>
          </span>
        </div>
        {loading ? (
          <p className="a-loading">Loading…</p>
        ) : decisions.length === 0 ? (
          <p className="a-empty">No decisions recorded yet.</p>
        ) : (
          <div className="a-scroll">
            <table className="a-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Decision</th>
                  <th>Reason</th>
                  <th>Deployment</th>
                </tr>
              </thead>
              <tbody>
                {decisions.map((d, i) => (
                  <tr key={`${d.job_id ?? i}-${i}`}>
                    <td className="a-table__dim" title={d.as_of}>{formatSince(d.as_of)}</td>
                    <td className={`a-state a-state--${(d.decision ?? "").toLowerCase()}`}>{d.decision ?? "—"}</td>
                    <td className="a-table__hi">{d.reason ?? "—"}</td>
                    <td className="a-table__dim">{d.deployment ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
